// High-level constraint model for the GCS — the editor-facing layer that sits
// on top of the pure solver (solver.js). It lets a UI store constraints against
// a polygon of vertices in a stable, human-shaped form and translates them into
// the solver's native `{ points, constraints }` sketch on demand.
//
// This module is a straight extraction of the model StenalPRO's sketch editor
// uses, decoupled from the editor: no THREE, no DOM, no app state — just data
// in, data out. Import it only if you want the same authoring UX (per-edge
// orientation/length/angle locks, vertex angles, point-to-point dimensions). If
// your program builds the solver's native format itself, you don't need this
// file — use solver.js directly.
//
// ---- Authoring model ------------------------------------------------------
//
// pts       : [ { x, y }, … ]            a polygon; edge i joins pts[i]→pts[i+1]
//                                        (wraps to pts[0] when `closed`)
// edgeCons  : [ { o, len, angle }, … ]   one entry per edge, same index as pts:
//                                          o:     'h' | 'v' | null   orientation lock
//                                          len:   number | null      locked length (mm)
//                                          angle: number | null      absolute angle (°)
// relations : [ { type, … }, … ]         relational/vertex constraints (see REL_META):
//                                          edge relations: { type, edges:[i,j] }
//                                          symmetric:      { type:'symmetric', edges:[mirrorEdge], points:[vi,vj] }
//                                          vertex dims:    { type:'hDistance'|'vDistance'|'distancePP', verts:[vi,vj], value }
//                                          coord lock:     { type:'lockX'|'lockY', vert, value }
//                                          midpoint:       { type:'midpoint', verts:[m, a, b] }
//                                          vertex angle:   { type:'angleV', vert, value }   (signed °)
//
// Vertices are referenced by INDEX in the authoring model (stable across a
// single solve). buildSketch() maps them to positional ids ('v0','v1',…) and to
// the solver's native constraint shapes, dropping any relation whose indices no
// longer exist (so a stale reference can't corrupt a solve after an edit).

// Metadata for each relation type — name/glyph for a UI, `on` (edge|vertex) for
// where it attaches, and `valued` when it carries an editable numeric value.
export const REL_META = {
  parallel:      { name: 'Parallel',      glyph: '∥', on: 'edge' },
  perpendicular: { name: 'Perpendicular', glyph: '⊥', on: 'edge' },
  equal:         { name: 'Equal',         glyph: '=', on: 'edge' },
  collinear:     { name: 'Collinear',     glyph: '≡', on: 'edge' },
  symmetric:     { name: 'Symmetric',     glyph: '⋈', on: 'edge' },
  hDistance:     { name: 'H distance',    glyph: '↦', on: 'vertex', valued: true },
  vDistance:     { name: 'V distance',    glyph: '↥', on: 'vertex', valued: true },
  distancePP:    { name: 'Distance',      glyph: '⤢', on: 'vertex', valued: true },
  lockX:         { name: 'Lock X',        glyph: '⇲', on: 'vertex', valued: true },
  lockY:         { name: 'Lock Y',        glyph: '⇱', on: 'vertex', valued: true },
  midpoint:      { name: 'Midpoint',      glyph: '◑', on: 'vertex' },
  angleV:        { name: 'Angle',         glyph: '∠', on: 'vertex', valued: true },
};

// The native constraint types the solver understands (see solver.js header).
export const SOLVER_CONSTRAINTS = [
  'horizontal', 'vertical', 'distance', 'angle', 'parallel', 'perpendicular',
  'equal', 'collinear', 'pointOnLine', 'symmetric', 'coincident',
  'hDistance', 'vDistance', 'lockX', 'lockY', 'midpoint', 'angleBetween',
];

// Translate the authoring model into the solver's native sketch. Returns:
//   { ids, sketch: { points, constraints }, edges }
// - ids:    positional point ids, ids[i] === 'v'+i
// - sketch: pass straight to solveSketch()/analyzeSketch()
// - edges:  polygon edges as id pairs — pass as solveSketch's opts.angleEdges to
//           prefer keeping angles between segments on a constraint edit.
// `closed` (default true) wraps the last edge back to vertex 0; set false for an
// open polyline (edge count = pts.length - 1).
export function buildSketch({ pts, edgeCons = [], relations = [], closed = true } = {}) {
  const n = pts.length;
  const ids = pts.map((_, i) => 'v' + i);
  const points = pts.map((p, i) => ({ id: ids[i], x: p.x, y: p.y, fixed: !!p.fixed }));
  const lastEdge = closed ? n : n - 1;
  const edge = (i) => [ids[i], ids[(i + 1) % n]];
  const constraints = [];
  let cid = 0;

  edgeCons.forEach((c, i) => {
    if (!c || i >= lastEdge) return;
    if (c.o === 'h') constraints.push({ id: 'c' + cid++, type: 'horizontal', edges: [edge(i)] });
    if (c.o === 'v') constraints.push({ id: 'c' + cid++, type: 'vertical', edges: [edge(i)] });
    if (c.len != null) constraints.push({ id: 'c' + cid++, type: 'distance', edges: [edge(i)], value: c.len });
    if (c.angle != null) constraints.push({ id: 'c' + cid++, type: 'angle', edges: [edge(i)], value: c.angle });
  });

  const vok = (vi) => vi >= 0 && vi < n;
  const eok = (ei) => ei >= 0 && ei < lastEdge;
  relations.forEach((r) => {
    if (!r || typeof r.type !== 'string') return;
    // Drop any relation whose referenced entities no longer exist.
    if (r.edges && r.edges.some((ei) => !eok(ei))) return;
    if (r.points && r.points.some((pi) => !vok(pi))) return;
    if (r.verts && r.verts.some((vi) => !vok(vi))) return;
    if (r.vert != null && !vok(r.vert)) return;
    const con = { id: 'r' + cid++, type: r.type };
    if (r.edges) con.edges = r.edges.map((ei) => edge(ei));
    if (r.points) con.points = r.points.map((pi) => ids[pi]);
    switch (r.type) {
      case 'hDistance': case 'vDistance': con.edges = [[ids[r.verts[0]], ids[r.verts[1]]]]; con.value = r.value; break;
      case 'distancePP': con.type = 'distance'; con.edges = [[ids[r.verts[0]], ids[r.verts[1]]]]; con.value = r.value; break;
      case 'lockX': case 'lockY': con.points = [ids[r.vert]]; con.value = r.value; break;
      case 'midpoint': con.points = r.verts.map((vi) => ids[vi]); break;
      // Angle at vertex k between its two incident edges: prev, k, next.
      case 'angleV': con.type = 'angleBetween'; con.points = [ids[(r.vert - 1 + n) % n], ids[r.vert], ids[(r.vert + 1) % n]]; con.value = r.value; break;
      default: break;
    }
    constraints.push(con);
  });

  const edges = [];
  for (let i = 0; i < lastEdge; i++) edges.push(edge(i));
  return { ids, sketch: { points, constraints }, edges };
}

// Signed angle (degrees, (−180,180]) of the corner at vertex k of a closed
// polygon — from ray (k→prev) to ray (k→next). The magnitude is the corner
// angle a user sees; the sign is the winding. Feed the value into an `angleV`
// relation to lock the corner. Returns null when a neighbour edge is degenerate.
export function cornerAngle(pts, k) {
  const n = pts.length;
  const v = pts[k], p = pts[(k - 1 + n) % n], w = pts[(k + 1) % n];
  const u1x = p.x - v.x, u1y = p.y - v.y, u2x = w.x - v.x, u2y = w.y - v.y;
  const l1 = Math.hypot(u1x, u1y), l2 = Math.hypot(u2x, u2y);
  if (l1 < 1e-9 || l2 < 1e-9) return null;
  const cross = (u1x * u2y - u1y * u2x) / (l1 * l2);
  const dot = (u1x * u2x + u1y * u2y) / (l1 * l2);
  return Math.atan2(cross, dot) * 180 / Math.PI;
}
